<template>
  <div class="language-snapshot">
    <SummarySkeletonLoader
      :is-loading="pageLoading && selectedFilters"
      :number-of-metrics="4"
    />
    <div
      v-if="cards.length"
      class="d-flex"
    >
      <div class="column">
        <div>
          <LanguageSnapshotCard
            v-for="card in cards.filter((_, i) => i % 2 === 0)"
            :key="card.name"
            v-bind="card"
          />
        </div>
      </div>
      <div class="column ml-3">
        <div>
          <LanguageSnapshotCard
            v-for="card in cards.filter((_, i) => i % 2 !== 0)"
            :key="card.name"
            v-bind="card"
          />
        </div>
      </div>
    </div>
    <NoDataBlock v-else>
      <template #noData>
        No Data Available
      </template>
      <template #description>
        There is no data available for your filter. You may change the filter to see the analysis.
      </template>
    </NoDataBlock>
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia';
import NoDataBlock from '@/components/library/NoDataBlock.vue';
import SummarySkeletonLoader from '@/components/shared/SkeletonLoaders/SummarySkeletonLoader.vue';
import { isEmpty } from 'lodash-es';
import { useAppLoading } from '@/stores/app/loading';
import { useLanguageSnapshot } from '@/stores/insights/languageSnapshot';
import LanguageSnapshotCard from '@/components/insights/OperationalInsights/LanguageSnapshot/LanguageSnapshotCard.vue';
import { useAppBase } from '@/stores/app/base';
import { useFilters } from '@/stores/filters/filters';
import { useDatePicker } from '@/stores/filters/datePicker';
import { subscribeToFilterUpdates, unsubscribeToFilterUpdates } from '@/helpers/filterDispatcher';
import { useInsights } from '@/stores/insights/insights';
import { useInsightsShare } from '@/stores/insights/share';

export default {
  name: 'LanguageSnapshot',

  components: {
    LanguageSnapshotCard,
    NoDataBlock,
    SummarySkeletonLoader,
  },
  emits: ['track-page-load'],
  computed: {
    ...mapState(useAppLoading, ['pageLoading']),
    ...mapState(useLanguageSnapshot, ['languageBlocks', 'totals', 'isGreenhouse']),
    ...mapState(useFilters, ['mergedTempFilters', 'myJobsEnabled']),
    ...mapState(useDatePicker, ['dateWeekIndex']),
    selectedFilters() {
      return !isEmpty(this.mergedTempFilters) || this.myJobsEnabled;
    },
    cards() {
      if (this.pageLoading) {
        // four skeleton cards
        return Array(4).fill({ isLoading: true });
      }

      return this.languageBlocks.map(
        ({
          block_color: color,
          block_examples: examples,
          block_message: message,
          block_name: name,
          block_per_job: perJob,
          block_percentage: percentage,
          block_top_departments: topDepartments,
          block_top_tags: topTags,
          block_total_jobs: totalJobs,
        }) => ({
          isLoading: this.pageLoading,
          topPlaces: this.isGreenhouse ? topDepartments : topTags,
          overallTotalJobs: this.totals.total_jobs_open,
          color,
          examples,
          message,
          name,
          perJob,
          percentage,
          totalJobs,
        }),
      );
    },
  },

  async mounted() {
    this.setShowInternalDefault(true);

    subscribeToFilterUpdates(this.loadPage);

    this.$emit('track-page-load', { page: 'Language Snapshot' });
    this.setFilterConfig({
      module: 'analytics/languageSnapshot',
      datepicker: true,
      showMyJobs: !window?.sessionStorage?.reportingShareKey,
      subMenuType: null,
    });

    const isSharedPage = window.sessionStorage.reportingShareKey;

    this.showFilterBarSettings({ route: this.$route.name, options: [] });

    if (!isSharedPage) {
      this.setShareConfig({
        shareable: true,
      });
    }

    await this.loadPage({
      router: this.$router,
      routeQuery: this.$route.query,
      selectedMyJobs: this.$route.query?.owned === 'true',
      selectedDateWeek: this.dateWeekIndex || this.$route.query?.week,
    });
    this.setDatePickerVariant({ variant: 'week' });
  },

  beforeUnmount() {
    unsubscribeToFilterUpdates();

    this.resetShareConfig();

    this.clearPersistentFilters();
    this.setDatePickerVariant({ resetDateRange: true });
  },

  methods: {
    ...mapActions(useLanguageSnapshot, ['loadPage']),
    ...mapActions(useFilters, ['clearPersistentFilters', 'setFilterConfig']),
    ...mapActions(useDatePicker, ['setDatePickerVariant']),
    ...mapActions(useAppBase, ['showFilterBarSettings']),
    ...mapActions(useInsights, ['setShowInternalDefault']),
    ...mapActions(useInsightsShare, ['setShareConfig', 'resetShareConfig']),
  },
};
</script>

<style scoped lang="less">
.language-snapshot {
  .column {
    width: 100%;
  }
}
</style>
